import { composeSwarmSelection, matchingSwarmTemplate, monsterTemplateLayers } from '../janus-engine/advancement/swarm-selection';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import type { creatureDefinitionSelection } from './spawn-browser';

type Row = Record<string, unknown>;
interface SelectionInputs { templates: Row[]; layers: Row[]; creatures: Row[]; suggested_name?: string }
export async function creatureSwarmDefinition(...args: Parameters<typeof creatureDefinitionSelection>): ReturnType<typeof creatureDefinitionSelection> {
    const bridge = window.ftCreatureDefinitionSelection ?? window.parent?.ftCreatureDefinitionSelection;
    if (!bridge) throw new Error('modify-swarm:source-reader-unavailable');
    return bridge(...args);
}
export async function monsterTemplateSelectionInputs(itemId: string, templateId: string, signal: AbortSignal, nameRoot = ''): Promise<SelectionInputs> {
    const response = await fetch(BASE_URL + 'options/tools/monster_templates/selection_inputs_inc.php', {
        method: 'POST', credentials: 'same-origin', signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_creature_item_id: itemId, monster_template_id: templateId, name_root: nameRoot }),
    });
    if (!response.ok) throw new Error('modify-template:selection-inputs-unavailable');
    return response.json() as Promise<SelectionInputs>;
}
export function selectedTemplateDetails(inputs: SelectionInputs, itemId: string, templateId: string): { label: string; crossCreature: boolean } {
    const template = inputs.templates.find(row => String(readItemField(row, 'monster_template_id')).toUpperCase() === templateId.toUpperCase());
    if (!template) throw new Error('modify-template:template-unavailable');
    const base = String(readItemField(template, 'base_creature_item_id') ?? '');
    const creature = inputs.creatures.find(row => String(readItemField(row, 'item_id')).toUpperCase() === base.toUpperCase());
    return { label: `${String(readItemField(template, 'monster_template_name') ?? templateId)} — built for ${String(readItemField(creature ?? null, 'item_name') ?? base)}`,
        crossCreature: base.toUpperCase() !== itemId.toUpperCase() };
}
export async function selectCreatureSwarm(itemId: string, currentTemplate: string, quantity: number, creatureName: string, signal: AbortSignal): Promise<string> {
    const root = creatureName + (quantity ? ` Swarm: ${quantity}` : '');
    const inputs = await monsterTemplateSelectionInputs(itemId, currentTemplate, signal, root);
    if (currentTemplate && !inputs.templates.some(row => readItemField(row, 'monster_template_id') === currentTemplate))
        throw new Error('modify-swarm:current-template-unavailable');
    const selection = composeSwarmSelection(itemId, monsterTemplateLayers(inputs.layers, currentTemplate), quantity);
    if (!selection.layers.length) return '';
    const existing = matchingSwarmTemplate(selection, inputs.templates, inputs.layers);
    if (existing) return existing;
    signal.throwIfAborted();
    const response = await fetch(BASE_URL + 'options/tools/monster_templates/create_monster_template_inc.php', {
        method: 'POST', credentials: 'same-origin', signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_creature_item_id: selection.base_creature_item_id,
            monster_template_name: inputs.suggested_name, browser_template_layers: selection.layers }),
    });
    if (!response.ok) throw new Error('modify-swarm:create-failed');
    const html = await response.text();
    const id = /id="input_hidden_monster_template_id_([A-Fa-f0-9-]+)"/.exec(html)?.[1]?.toUpperCase();
    if (!id) throw new Error('modify-swarm:create-failed');
    // Also makes a mixed deployment safe: legacy layer-only dedup must never silently choose another base.
    const saved = await monsterTemplateSelectionInputs(itemId, id, signal);
    if (matchingSwarmTemplate(selection, saved.templates.filter(row => readItemField(row, 'monster_template_id') === id), saved.layers) !== id)
        throw new Error('modify-swarm:saved-template-mismatch');
    return id;
}
